import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare, X, Send, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { getSessionId } from "../lib/track";
import { getLeadSource } from "../lib/leadSource";
import { SITE } from "../lib/site";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatWidget() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [replyVia, setReplyVia] = useState("email");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  if (pathname === "/admin") return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.message.trim().length < 10) {
      setError("Please write a few words about your project (10+ characters).");
      return;
    }
    if (replyVia === "whatsapp" && form.phone.replace(/\D/g, "").length < 7) {
      setError("Please add your WhatsApp number so we can reply there.");
      return;
    }
    setSending(true);
    try {
      const resp = await fetch(`${API}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.name.trim(),
          email: form.email.trim(),
          phone: replyVia === "whatsapp" ? form.phone.trim() : null,
          topic: replyVia === "whatsapp" ? "Chat — WhatsApp reply requested" : "Chat — email reply",
          project_description: form.message.trim(),
          service: "Other Survey/GIS Requirement",
          consent: true,
          source: "chat",
          session_id: getSessionId(),
          lead_source: getLeadSource(),
        }),
      });
      if (!resp.ok) throw new Error("send failed");
      setSent(true);
    } catch {
      setError("Could not send right now — please email cad@geodrafts.com directly.");
    }
    setSending(false);
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3" data-testid="chat-widget">
      {/* WhatsApp quick-chat — thumb-reach shortcut for mobile visitors */}
      <a
        href={SITE.whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-button"
        aria-label={`Chat on WhatsApp ${SITE.whatsappNumber}`}
        className="lg:hidden flex items-center justify-center h-12 w-12 rounded-full bg-[#25D366] text-white shadow-[0_10px_28px_rgba(37,211,102,0.45)] hover:scale-105 transition-transform"
      >
        <MessageCircle className="h-6 w-6" />
      </a>
      {open && (
        <div
          className="mb-3 w-[320px] sm:w-[360px] bg-navy border border-navy-light rounded-md shadow-[0_24px_60px_rgba(11,29,47,0.45)] overflow-hidden"
          data-testid="chat-widget-panel"
        >
          <div className="flex items-center justify-between px-5 py-3.5 bg-navy-deep border-b border-navy-light">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-aqua opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-aqua" />
              </span>
              <div>
                <p className="font-display font-bold text-white text-sm leading-none">Chat With GeoDrafts</p>
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-aqua/80">Replies land in your email</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close" aria-label="Close chat" className="text-white/50 hover:text-white transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {sent ? (
            <div className="px-5 py-8 text-center" data-testid="chat-widget-success">
              <CheckCircle2 className="h-9 w-9 text-aqua mx-auto" />
              <p className="mt-4 font-display font-bold text-white">Message sent</p>
              <p className="mt-2 text-sm text-white/65 leading-relaxed">
                {replyVia === "whatsapp" ? (
                  <>Thanks {form.name.split(" ")[0]} — we'll message you on WhatsApp at <span className="text-aqua">{form.phone}</span> shortly.</>
                ) : (
                  <>Thanks {form.name.split(" ")[0]} — your note is with the drafting team. We'll reply to <span className="text-aqua">{form.email}</span> shortly.</>
                )}
              </p>
              <button
                onClick={() => {
                  setSent(false);
                  setForm({ name: "", email: "", phone: "", message: "" });
                  setReplyVia("email");
                  setOpen(false);
                }}
                data-testid="chat-widget-done"
                className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-aqua hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="px-5 py-5 space-y-3">
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Your name"
                data-testid="chat-widget-name"
                className="w-full bg-navy-deep border border-navy-light rounded-sm px-3.5 py-2.5 text-sm text-white placeholder-white/35 outline-none focus:border-aqua"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Your email — we reply here"
                data-testid="chat-widget-email"
                className="w-full bg-navy-deep border border-navy-light rounded-sm px-3.5 py-2.5 text-sm text-white placeholder-white/35 outline-none focus:border-aqua"
              />
              <textarea
                required
                rows={3}
                value={form.message}
                onChange={set("message")}
                placeholder="Tell us about your survey drafting project…"
                data-testid="chat-widget-message"
                className="w-full bg-navy-deep border border-navy-light rounded-sm px-3.5 py-2.5 text-sm text-white placeholder-white/35 outline-none focus:border-aqua resize-none"
              />
              <div>
                <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-white/40 mb-2">Reply to me via</p>
                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Reply channel">
                  {[
                    ["email", "Email"],
                    ["whatsapp", "WhatsApp"],
                  ].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={replyVia === key}
                      onClick={() => setReplyVia(key)}
                      data-testid={`chat-reply-${key}`}
                      className={`font-mono text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-2 rounded-sm border transition-all ${
                        replyVia === key ? "bg-aqua text-navy border-aqua" : "bg-navy-deep text-white/55 border-navy-light hover:border-aqua/50"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {replyVia === "whatsapp" && (
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Your WhatsApp number with country code"
                    data-testid="chat-widget-phone"
                    className="mt-2 w-full bg-navy-deep border border-navy-light rounded-sm px-3.5 py-2.5 text-sm text-white placeholder-white/35 outline-none focus:border-aqua"
                  />
                )}
              </div>
              {error && (
                <p className="text-xs text-red-400" data-testid="chat-widget-error" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={sending}
                data-testid="chat-widget-send"
                className="w-full inline-flex items-center justify-center gap-2 bg-aqua text-navy font-bold text-sm px-5 py-3 rounded-sm hover:bg-aqua-deep transition-colors disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send to the Drafting Team
              </button>
              <p className="font-mono text-[9px] text-white/35 text-center leading-relaxed">
                Sent straight to cad@geodrafts.com · by sending you agree to be contacted by email
              </p>
            </form>
          )}
        </div>
      )}

      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-widget-open"
          aria-label="Open chat"
          className="group flex items-center gap-2.5 bg-aqua text-navy rounded-full pl-4 pr-5 py-3.5 shadow-[0_12px_32px_rgba(32,196,199,0.4)] hover:bg-aqua-deep hover:shadow-[0_16px_40px_rgba(32,196,199,0.55)] transition-all"
        >
          <MessageSquare className="h-5 w-5" />
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em]">Chat — replies by email</span>
        </button>
      )}
    </div>
  );
}
